import { Component, Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive actions get a red button and a warning icon. Defaults to true: most confirmations here are deletes. */
  destructive?: boolean;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="confirm-head">
      @if (data.destructive !== false) {
        <span class="confirm-icon" aria-hidden="true"><mat-icon>warning</mat-icon></span>
      }
      <h2 mat-dialog-title>{{ data.title }}</h2>
    </div>
    <mat-dialog-content><p class="confirm-message">{{ data.message }}</p></mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [mat-dialog-close]="false">{{ data.cancelLabel || 'Annuler' }}</button>
      <button mat-flat-button type="button" [class.confirm-danger]="data.destructive !== false" [mat-dialog-close]="true">
        {{ data.confirmLabel || 'Supprimer' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .confirm-head { display: flex; align-items: center; gap: 12px; padding: 24px 24px 0; }
    .confirm-head h2 { margin: 0; padding: 0; color: var(--admin-ink); font-size: 18px; font-weight: 700; letter-spacing: -.015em; }
    .confirm-head h2::before { display: none; }
    .confirm-icon { display: grid; width: 40px; height: 40px; flex: 0 0 40px; place-items: center; border-radius: 50%; background: var(--admin-danger-soft); color: var(--admin-danger); }
    .confirm-message { margin: 0; color: var(--admin-muted); font-size: 14px; line-height: 1.55; }
    mat-dialog-actions { padding: 8px 24px 20px; gap: 8px; }
    .confirm-danger { --mat-button-filled-container-color: var(--admin-danger); --mat-button-filled-label-text-color: #fff; }
  `],
})
export class ConfirmDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) readonly data: ConfirmOptions) {}
}

/** Branded replacement for window.confirm(): resolves true only when the action is confirmed. */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  constructor(private readonly dialog: MatDialog) {}

  async confirm(options: ConfirmOptions): Promise<boolean> {
    const ref = this.dialog.open<ConfirmDialogComponent, ConfirmOptions, boolean>(ConfirmDialogComponent, {
      data: options,
      width: 'min(440px, calc(100vw - 32px))',
      autoFocus: 'dialog',
      restoreFocus: true,
    });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }
}
